import styled, { keyframes } from 'styled-components';
// ============================================
// KEYFRAME ANIMATIONS
// ============================================
const fadeUp = keyframes `
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;
const pulseGlow = keyframes `
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(184, 134, 11, 0.4);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(184, 134, 11, 0);
  }
`;
// ============================================
// PAGE LAYOUT
// ============================================
export const PageWrapper = styled.div `
  min-height: 100vh;
  background: var(--bg-primary);
`;
// ============================================
// HERO SECTION
// ============================================
export const HeroSection = styled.section `
  padding: 48px 24px 32px;
  text-align: center;
  background: linear-gradient(180deg, var(--bg-secondary) 0%, var(--bg-primary) 100%);
  border-bottom: 1px solid var(--border-color);
`;
export const HeroTitle = styled.h1 `
  font-family: 'Playfair Display', 'Adamina', serif !important;
  font-size: 38px !important;
  font-weight: 600 !important;
  letter-spacing: 0.03em;
  margin-bottom: 10px !important;
  color: var(--text-primary) !important;

  span {
    color: var(--accent-gold, #B8860B);
  }

  @media (max-width: 768px) {
    font-size: 28px !important;
  }
`;
export const HeroSubtitle = styled.p `
  color: var(--text-secondary);
  font-size: 15px;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;

  .admin-note {
    color: var(--accent-gold, #B8860B);
    font-weight: 500;
  }
`;
// ============================================
// STATS GRID
// ============================================
export const StatsGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  max-width: 1000px;
  margin: -24px auto 32px;
  padding: 0 24px;
  position: relative;
  z-index: 10;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;
export const StatCard = styled.div `
  background: var(--bg-primary);
  border: 1px solid ${props => props.$color || 'var(--border-color)'};
  padding: 20px;
  text-align: center;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${props => props.$color || 'var(--accent-gold, #B8860B)'};
    opacity: 0;
    transition: opacity 0.3s;
  }

  &:hover::before {
    opacity: 1;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  .stat-value {
    font-family: 'Playfair Display', 'Adamina', serif;
    font-size: 28px;
    font-weight: 600;
    color: ${props => props.$color || 'var(--accent-gold, #B8860B)'};
    margin-bottom: 4px;
  }

  .stat-label {
    color: var(--text-secondary);
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: 500;
  }
`;
// ============================================
// MAIN CONTENT
// ============================================
export const MainContent = styled.div `
  max-width: 1200px;
  margin: 0 auto;
  padding: 28px 24px;
`;
// ============================================
// FILTER SECTION
// ============================================
export const FilterSection = styled.div `
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  padding: 24px;
  margin-bottom: 32px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--accent-gold, #B8860B), var(--accent-gold-light, #D4A634));
  }
`;
export const FilterGroup = styled.div `
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }

  label {
    font-size: 12px;
    color: var(--text-secondary);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-right: 8px;
  }
`;
export const FilterPill = styled.button `
  padding: 8px 16px;
  background: ${props => props.$active ? 'var(--accent-gold-bg, rgba(184, 134, 11, 0.08))' : 'var(--bg-secondary)'};
  color: ${props => props.$active ? 'var(--accent-gold, #B8860B)' : 'var(--text-secondary)'};
  border: 1px solid ${props => props.$active ? 'var(--accent-gold, #B8860B)' : 'var(--border-color)'};
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    border-color: var(--accent-gold, #B8860B);
    color: ${props => props.$active ? 'var(--accent-gold, #B8860B)' : 'var(--text-primary)'};
    background: ${props => props.$active ? 'var(--accent-gold-bg, rgba(184, 134, 11, 0.08))' : 'var(--bg-tertiary)'};
  }

  svg {
    font-size: 12px;
  }
`;
// ============================================
// TIMELINE
// ============================================
export const UpdatesTimeline = styled.div `
  position: relative;
  padding-left: 40px;

  &::before {
    content: '';
    position: absolute;
    left: 11px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: linear-gradient(180deg, var(--accent-gold, #B8860B) 0%, var(--border-color) 100%);
  }

  @media (max-width: 768px) {
    padding-left: 24px;

    &::before {
      left: 7px;
    }
  }
`;
// ============================================
// UPDATE CARD
// ============================================
export const UpdateCard = styled.div `
  position: relative;
  background: var(--bg-primary);
  border: 1px solid ${props => props.$featured ? 'var(--accent-gold, #B8860B)' : 'var(--border-color)'};
  padding: 0;
  margin-bottom: 32px;
  transition: all 0.3s ease;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--accent-gold, #B8860B), var(--accent-gold-light, #D4A634));
    opacity: ${props => props.$featured ? 1 : 0};
    transition: opacity 0.3s;
  }

  &::after {
    content: '';
    position: absolute;
    left: -44px;
    top: 24px;
    width: 20px;
    height: 20px;
    background: var(--accent-gold, #B8860B);
    border: 4px solid var(--bg-primary);
    border-radius: 50%;
    z-index: 2;
    box-shadow: 0 0 0 2px var(--accent-gold, #B8860B);
  }

  &:hover {
    border-color: var(--accent-gold-border, rgba(184, 134, 11, 0.25));
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);

    &::before {
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    &::after {
      left: -28px;
      width: 14px;
      height: 14px;
      border-width: 3px;
    }
  }
`;
export const UpdateCardHeader = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding: 20px;
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-secondary);

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;
export const UpdateTitle = styled.h3 `
  font-family: 'Playfair Display', 'Adamina', serif !important;
  font-size: 18px !important;
  font-weight: 600 !important;
  color: var(--text-primary) !important;
  margin: 0 0 10px 0 !important;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: var(--accent-gold, #B8860B);
    font-size: 18px;
  }

  .featured-star {
    color: #fbbf24;
  }
`;
export const UpdateMeta = styled.div `
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
`;
export const CategoryBadge = styled.span `
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  background: ${props => props.$color}15;
  color: ${props => props.$color};
  border: 1px solid ${props => props.$color}40;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;
export const StatusBadge = styled(CategoryBadge) ``;
export const DateBadge = styled.span `
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--text-tertiary);

  svg {
    font-size: 12px;
  }
`;
export const AuthorBadge = styled.span `
  font-size: 12px;
  color: var(--text-tertiary);
`;
export const UpdateCardBody = styled.div `
  padding: 20px;
`;
export const UpdateContent = styled.div `
  color: var(--text-secondary);
  line-height: 1.7;
  font-size: 14px;

  p {
    margin: 0 0 12px 0;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;
export const TechnicalSection = styled.div `
  margin-top: 16px;
  padding: 16px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-left: 3px solid #3b82f6;

  h4 {
    color: #3b82f6;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin: 0 0 12px 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  p {
    color: var(--text-secondary);
    font-size: 13px;
    line-height: 1.6;
    margin: 0;
    font-family: 'JetBrains Mono', 'Courier New', monospace;
  }
`;
export const UpdateCardFooter = styled.div `
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  padding: 16px 20px;
  border-top: 1px solid var(--border-color);
  background: var(--bg-secondary);
`;
export const TagsContainer = styled.div `
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;
export const Tag = styled.span `
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  color: var(--text-secondary);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  svg {
    font-size: 10px;
  }
`;
export const ActionButtonsContainer = styled.div `
  display: flex;
  gap: 8px;
  margin-left: auto;
  align-items: center;
`;
export const TourButton = styled.button `
  background: linear-gradient(135deg, var(--accent-gold, #B8860B) 0%, var(--accent-gold-light, #D4A634) 100%);
  border: none;
  color: #FFFFFF;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-2px);
  }

  svg {
    font-size: 14px;
  }
`;
export const LinkButton = styled.a `
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: transparent;
  color: var(--accent-gold, #B8860B);
  border: 1px solid var(--accent-gold, #B8860B);
  text-decoration: none;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.2s;

  &:hover {
    background: var(--accent-gold, #B8860B);
    color: #FFFFFF;
  }

  svg {
    font-size: 11px;
  }
`;
// ============================================
// EMPTY STATE
// ============================================
export const EmptyState = styled.div `
  text-align: center;
  padding: 80px 24px;
  color: var(--text-tertiary);
  background: var(--bg-secondary);
  border: 1px dashed var(--border-color);

  svg {
    font-size: 64px;
    margin-bottom: 24px;
    opacity: 0.3;
    color: var(--accent-gold, #B8860B);
  }

  h3 {
    font-family: 'Playfair Display', 'Adamina', serif;
    font-size: 20px;
    color: var(--text-secondary);
    margin-bottom: 8px;
  }

  p {
    font-size: 14px;
    max-width: 400px;
    margin: 0 auto;
    line-height: 1.6;
  }
`;
// ============================================
// LOADING STATE
// ============================================
export const LoadingWrapper = styled.div `
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  text-align: center;
`;
export const LoadingSpinner = styled.div `
  width: 48px;
  height: 48px;
  border: 3px solid var(--border-color);
  border-top-color: var(--accent-gold, #B8860B);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;
export const LoadingText = styled.p `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: 18px;
  color: var(--text-secondary);
`;
